/* eslint-disable spire/export-styles */
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getWORData, getWORDeliveryInfoData } from "@tKgpco/Services/WORService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import { TYPOGRAPHY } from "@tKgpco/theme/design";
import { useWORCheckoutShippingFormContext } from "@tKgpco/Widgets/WOR/WORCheckoutShippingPage";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,

    USaddressErrorMessage: state?.KGPState.USPS.USvalidateErrorMessage,
});

interface WORCheckoutShippingStyles {
    warningTextCustom?: InjectableCss;
    warningSectionCustom?: InjectableCss;
    warningIconCustom?: IconPresentationProps;
    errorMessageGridItem?: GridItemProps;
}

export const worCheckoutShippingStyles: WORCheckoutShippingStyles = {
    warningIconCustom: {
        src: "AlertCircle",
        color: "#f0b003",
        css: css`
            margin-right: 10px;
        `,
    },
    warningSectionCustom: {
        css: css`
            display: flex;
            align-items: flex-start;
            border: 2px solid #f0b003;
            padding: 10px 10px;
            margin-bottom: 5px;
            .errorList {
                list-style-type: disc;
                margin-left: 1rem;
                li {
                    color: #f0b003;
                }
            }
        `,
    },
    warningTextCustom: {
        css: css`
            font-size: 15px;
            color: #f0b003;
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    errorMessageGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
};
type Props = WidgetProps & ReturnType<typeof mapStateToProps>;
const styles = worCheckoutShippingStyles;
const WORCheckoutShippingContinueButton: React.FunctionComponent<Props> = ({
    pageTitle,
    fields,
    USaddressErrorMessage,
}: Props) => {
    const [initialized, setInitialized] = React.useState(false);
    const { useOneTimeAddress, formState, formErrors, dispatch, worDeliveryInfo } = useWORCheckoutShippingFormContext();
    const [deliveryInfoError, setDeliveryInfoError] = React.useState(false);
    // Disallowed special characters
    const forbiddenChars = /[^a-zA-Z0-9\s]/;
    // Check for overly long spacing (more than one consecutive space)
    const excessiveSpacing = / {2,}/;
    const emailRegex = /[^a-zA-Z0-9\s@.]/;

    React.useEffect(() => {
        if (worDeliveryInfo?.updated && Object?.keys(worDeliveryInfo?.updated)?.length) {
            const phoneNumber = worDeliveryInfo.updated.phone.split("-").join("");

            const isInValid =
                !worDeliveryInfo.updated.firstName.trim() ||
                !worDeliveryInfo.updated.lastName.trim() ||
                !worDeliveryInfo.updated.phone ||
                !Number(phoneNumber) ||
                phoneNumber.length !== 10 ||
                forbiddenChars.test(worDeliveryInfo.updated.firstName) ||
                excessiveSpacing.test(worDeliveryInfo.updated.firstName) ||
                forbiddenChars.test(worDeliveryInfo.updated.lastName) ||
                excessiveSpacing.test(worDeliveryInfo.updated.lastName) ||
                (worDeliveryInfo.updated.email && emailRegex.test(worDeliveryInfo.updated.email));

            setDeliveryInfoError(isInValid);
        }
    }, [worDeliveryInfo]);

    const isDisabled = useOneTimeAddress ? Object.keys(formErrors)?.length > 0 : deliveryInfoError;
    const worData = getWORData();
    const StyledSection = getStyledWrapper("section");

    return (
        <GridContainer>
            <GridItem
                width={12}
                css={css`
                    background-color: #f1f6fb;
                `}
            >
                <Typography>
                    The materials designated for release will be shipped to shipping address. If you select Ship to
                    One-Time Address, you can override the default shipping address.
                </Typography>
            </GridItem>
            {Object.keys(worData).length === 0 && (
                <GridItem width={12}>
                    <StyledSection {...styles.warningSectionCustom}>
                        <Icon {...styles.warningIconCustom}></Icon>
                        <Typography {...styles.warningTextCustom}>
                            {siteMessage("releaseMaterialWarningMessage")}
                        </Typography>
                    </StyledSection>
                </GridItem>
            )}
            <GridItem width={12}>
                <Button
                    css={css`
                        width: 100%;
                        border-radius: 1.5em;
                    `}
                    disabled={isDisabled || Object.keys(worData).length === 0}
                >
                    Continue
                </Button>
            </GridItem>
            {useOneTimeAddress && USaddressErrorMessage ? (
                <GridItem {...styles.errorMessageGridItem}>
                    <Typography style={{ fontWeight: 600, color: "red", textAlign: "center" }}>
                        {USaddressErrorMessage}
                    </Typography>
                </GridItem>
            ) : null}
        </GridContainer>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(WORCheckoutShippingContinueButton),
    definition: {
        group: "Basic",
        icon: "heading",
    },
};
export default widgetModule;
